import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { CACHE_KEY_PHARMAKON_PENDING_PRESCRIPTION_TRANSFER } from 'src/app/hooks/data/_keys';
import { useBffClient } from 'src/app/hooks/data/useBffClient';
import type { HomeItem } from 'src/app/types';

export const usePendingPrescriptionTransfers = (
  accountId: number,
  enabled?: boolean
): UseQueryResult<HomeItem[], AxiosError> => {
  const authClient = useBffClient();

  return useQuery(
    [CACHE_KEY_PHARMAKON_PENDING_PRESCRIPTION_TRANSFER, accountId],
    () =>
      authClient.get(`home/${accountId}/pending_items?type=transfer`).then((resp: { data: HomeItem[] }) => resp.data),
    {
      ...(enabled !== undefined && { enabled }),
    }
  );
};

export interface TransferSuccess {
  transfers: TransferResponse[];
}

export interface TransferResponse {
  id: string;
}

export interface FormPharmacy {
  pharmacyName: string;
  pharmacyPhone: string;
  pharmacyStreets: string;
}

export interface BaseTransferMedication {
  medicationName: string;
  hiclSeqno?: string;
  source: 'pharmacy' | 'doctor';
}

export interface PharmacyTransferMedication extends BaseTransferMedication {
  pharmacyDetails?: string;
  pharmacyName: string;
  pharmacyPhone?: string;
}

export interface DoctorTransferMedication extends BaseTransferMedication {
  doctorName: string;
  doctorPhone: string;
  npi?: string;
}

export type TransferMedication = PharmacyTransferMedication | DoctorTransferMedication;

export interface SearchedMedication {
  name: string;
  hiclSeqno?: string;
}

export interface TransferMedicationPayload {
  patientId: string;
  transfers: TransferMedication[];
}

export const useCreateTransferTask = (): UseMutationResult<TransferSuccess, AxiosError, TransferMedicationPayload> => {
  const authClient = useBffClient();

  return useMutation(payload =>
    authClient.post(`transfers/prescriptions`, payload).then((resp: { data: TransferSuccess }) => resp.data)
  );
};

export const useNotifyPharmacy = (accountId: string): UseMutationResult<TransferSuccess, AxiosError> => {
  const authClient = useBffClient();

  return useMutation(() => authClient.post(`accounts/${accountId}/notify_transfer`).then(resp => resp.data));
};

export interface HealthProfileTransferMedicationPayload {
  patientId: string;
  externalMedicationIds: string[];
  searchedMedications: SearchedMedication[];
}

export const useCreateHealthProfileTransferTask = (): UseMutationResult<
  TransferSuccess,
  AxiosError,
  HealthProfileTransferMedicationPayload
> => {
  const authClient = useBffClient();
  return useMutation(payload =>
    authClient.post(`transfers/health_profile`, payload).then((resp: { data: TransferSuccess }) => resp.data)
  );
};
